import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';

import { ColorStyleProps } from '../../../@types/theme';
import { isThemeColorName } from '../../../utils';
import Text from '../Text';

import styles from './BadgeWithIcon.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  content: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  color?: ColorStyleProps | `#${string}`;
  textColor?: ColorStyleProps | `#${string}`;
  disabled?: boolean;
  icon?: IconProp;
  isActive?: boolean;
}

export const BadgeWithIcon: React.FunctionComponent<Props> = ({
  className,
  content,
  color = 'primary',
  textColor = 'white',
  onClick,
  disabled,
  icon,
  isActive: controlledIsActive,
  ...htmlElementProps
}) => {
  const [isActive, setIsActive] = React.useState(true);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setIsActive(
        controlledIsActive !== undefined ? !controlledIsActive : !isActive,
      );
      if (onClick) {
        onClick(event);
      }
    }
  };

  const cls = classNames(
    styles.wrapper,
    { [styles.disabled]: disabled },
    className,
  );

  return (
    <div
      role="button"
      tabIndex={0}
      className={cls}
      onClick={handleClick}
      aria-hidden="true"
      {...htmlElementProps}
    >
      <span
        className={classNames(styles['icon-wrapper'], {
          [styles['is-active']]: (controlledIsActive ?? isActive) && !disabled,
          [`bg-${color}`]:
            (controlledIsActive ?? isActive) &&
            !disabled &&
            isThemeColorName(color),
          [`text-${textColor}`]: isThemeColorName(textColor),
          'border-0': (controlledIsActive ?? isActive) && !disabled,
        })}
        style={{
          backgroundColor:
            (controlledIsActive ?? isActive) &&
            !disabled &&
            !isThemeColorName(color)
              ? color
              : undefined,
          color: !isThemeColorName(textColor) ? textColor : undefined,
        }}
      >
        {icon && (controlledIsActive ?? isActive) && !disabled && (
          <FontAwesomeIcon icon={icon} />
        )}
      </span>

      <Text className={styles['badge-label']} size="sm1">
        {content}
      </Text>
    </div>
  );
};

export default BadgeWithIcon;
